import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Users, Star, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { socialProofData } from '../data/mock';

const iconMap = {
  'MapPin': MapPin,
  'Users': Users,
  'Star': Star
};

const SocialProofSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="social-proof" className="relative py-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={socialProofData.backgroundImage} 
          alt="Operations"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-green-900/85" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {socialProofData.title}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {socialProofData.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {socialProofData.stats.map((stat, index) => {
              const Icon = iconMap[stat.icon];
              return (
                <div
                  key={index}
                  className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center transform transition-all duration-500 hover:scale-105 ${
                    isVisible 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100 + 200}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-xl mb-4 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-5xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xl font-semibold text-green-400 mb-2">
                    {stat.label}
                  </div>
                  <p className="text-gray-300">
                    {stat.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Google Reviews CTA */}
          <div className={`text-center transform transition-all duration-700 delay-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <Button
              asChild
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-2xl"
            >
              <a 
                href={socialProofData.reviewLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center"
              >
                <Star className="mr-2 w-5 h-5 fill-yellow-400 text-yellow-400" />
                View Our Google Reviews
                <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
